import { displayName } from '../map/map-geometry';
import { Finding, SuburbGraph, groupCrossState } from '../rules/suburb-map-checks';
import { SubRegionFinding } from '../rules/suburb-subregion-edits';

/**
 * The Checks section's report (#256, #260): the map checks' findings as rows a person can click (each flies to a
 * suburb) and a one-line summary. Pure, tested in check-report.spec.ts.
 */

export interface CheckRow {
  kind: 'enclave' | 'split' | 'cross-state' | 'offshore';
  text: string;
  /** The suburb a click flies to. */
  code: string;
}

export interface CheckReport {
  rows: CheckRow[];
  summary: string;
  /** Enclaves surrounded by one region only: "Fill N unanimous enclaves" can fill them. */
  fixable: number;
}

/** How the report names regions and sub-regions, and a region's declared state. */
export interface CheckNames {
  region(id: string): string;
  subRegion(id: string): string;
  regionState(id: string): string | null | undefined;
}

/** More rows than this aren't worth listing: fix the first ones and run the checks again. */
export const MAX_CHECK_ROWS = 300;

const s = (n: number) => (n === 1 ? '' : 's');

export function checkReport(
  findings: Finding[],
  subRegionFindings: SubRegionFinding[],
  graph: SuburbGraph,
  names: CheckNames
): CheckReport {
  const where = (code: string) => {
    const [name, st] = graph.nodes[code] ?? [code, '?'];
    return `${displayName(name)} (${st})`;
  };
  const regions = regionRows(findings, where, names);
  const subRegions = subRegionRows(subRegionFindings, where, names);
  return {
    rows: [...regions.rows, ...subRegions.rows].slice(0, MAX_CHECK_ROWS),
    summary: regions.summary + subRegions.summary,
    fixable: findings.filter((f) => f.kind === 'enclave' && f.fix).length,
  };
}

/** Region findings: enclaves, then splits, then cross-state groups, then offshore pieces (information only). */
function regionRows(
  f: Finding[],
  where: (code: string) => string,
  names: CheckNames
): { rows: CheckRow[]; summary: string } {
  const nm = (id: string) => names.region(id);
  const counts = { enclave: 0, island: 0, offshore: 0, 'cross-state': 0 };
  for (const x of f) counts[x.kind]++;
  const groups = groupCrossState(f);
  const offshore = counts.offshore ? ` · ${counts.offshore} offshore (fine)` : '';
  const summary = f.length
    ? `${counts.enclave} enclaves · ${counts.island} split · ${counts['cross-state']} cross-state in ${groups.length} group${s(groups.length)}${offshore}`
    : 'No issues found.';
  const rows: CheckRow[] = [];
  for (const x of f)
    if (x.kind === 'enclave')
      rows.push({
        kind: 'enclave',
        code: x.code,
        text: `${where(x.code)}: surrounded by ${x.regions.map(nm).join(' + ')}`,
      });
  for (const x of f) {
    if (x.kind === 'island')
      rows.push({
        kind: 'split',
        code: x.code,
        text: `${nm(x.region)}: ${x.size} suburb${s(x.size)} cut off near ${where(x.code)}`,
      });
  }
  for (const g of groups) {
    const first = g.codes[0];
    const text = `${nm(g.region)} (${names.regionState(g.region) ?? '?'}) has ${g.codes.length} suburb${s(g.codes.length)} in ${g.state}, e.g. ${where(first)}`;
    rows.push({ kind: 'cross-state', code: first, text });
  }
  for (const x of f) {
    if (x.kind === 'offshore')
      rows.push({
        kind: 'offshore',
        code: x.code,
        text: `${nm(x.region)}: ${x.size} on an island near ${where(x.code)}; no land border, fine`,
      });
  }
  return { rows: rows.slice(0, MAX_CHECK_ROWS), summary };
}

/** Sub-region findings, named by sub-region: split and enclave are worth a look, offshore is information. */
function subRegionRows(
  f: SubRegionFinding[],
  where: (code: string) => string,
  names: CheckNames
): { rows: CheckRow[]; summary: string } {
  const rows = f.map((x): CheckRow => {
    const sr = `${names.subRegion(x.subRegionId)} (sub-region)`;
    if (x.kind === 'enclave')
      return {
        kind: 'enclave',
        code: x.code,
        text: `${sr}: ${where(x.code)} is surrounded by its suburbs but not in it`,
      };
    if (x.kind === 'island')
      return {
        kind: 'split',
        code: x.code,
        text: `${sr}: ${x.size} suburb${s(x.size)} cut off near ${where(x.code)}`,
      };
    return {
      kind: 'offshore',
      code: x.code,
      text: `${sr}: ${x.size} on an island near ${where(x.code)}; fine`,
    };
  });
  if (!rows.length) return { rows, summary: '' };
  const n = (k: SubRegionFinding['kind']) => f.filter((x) => x.kind === k).length;
  const offshore = n('offshore') ? `, ${n('offshore')} offshore` : '';
  return {
    rows,
    summary: ` · sub-regions: ${n('enclave')} enclaves, ${n('island')} split${offshore}`,
  };
}
